import { CSSProperties } from "react";

import { createTheme, ThemeOptions } from "@mui/material";
import { divider as dividerTokens } from "theme/tokens/borders";
import { buttonTokens } from "theme/tokens/button";
import { palette as colorPalette } from "theme/tokens/colors";
import { focusRing } from "theme/tokens/focus";
import { iconColor, iconSize } from "theme/tokens/iconography";
import { breakpoints as breakpointTokens } from "theme/tokens/layout";
import { duration } from "theme/tokens/motion";
import { radii } from "theme/tokens/radii";
import { color } from "theme/tokens/semantic/color";
import { layout } from "theme/tokens/semantic/layout";
import { motion as motionRoles } from "theme/tokens/semantic/motion";
import { shadows } from "theme/tokens/shadows";
import { spacingUnit } from "theme/tokens/spacing";
import { typography, typographyTokens } from "theme/tokens/typography";
import { zIndex as zIndexTokens } from "theme/tokens/zIndex";

import { cancelColor, primaryColor, secondaryColor } from "assets/colors";

const dialogTitlePadding = `${layout.dialog.titlePaddingY}px ${layout.dialog.paddingX}px`;
const dialogBodyPadding = `${layout.dialog.bodyPaddingY}px ${layout.dialog.paddingX}px`;
const dialogActionsPadding = `${layout.dialog.actionsPaddingY}px ${layout.dialog.paddingX}px`;

declare module "@mui/material/styles/createPalette" {
  interface Palette {
    cancel: Palette["primary"];
    default: Palette["primary"];
    inputButton: Palette["primary"];
  }
  interface PaletteOptions {
    cancel: PaletteOptions["primary"];
    default: PaletteOptions["primary"];
    inputButton: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    inputButton: true;
  }
}

declare module "@mui/material/styles" {
  interface TypographyVariants {
    display: CSSProperties;
    title: CSSProperties;
    heading: CSSProperties;
    subheading: CSSProperties;
    label: CSSProperties;
    placeholder: CSSProperties;
    helper: CSSProperties;
    error: CSSProperties;
  }

  interface TypographyVariantsOptions {
    display?: CSSProperties;
    title?: CSSProperties;
    heading?: CSSProperties;
    subheading?: CSSProperties;
    label?: CSSProperties;
    placeholder?: CSSProperties;
    helper?: CSSProperties;
    error?: CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    display: true;
    title: true;
    heading: true;
    subheading: true;
    label: true;
    placeholder: true;
    helper: true;
    error: true;
  }
}

const contrastWhite = color.surface.default;

export const appThemeOptions: ThemeOptions = {
  typography: {
    // MUI built-in scale (h1–h6, body1/2, caption, button) preserves the
    // current look; custom variants extend it for the src/ui/Text system.
    ...typography,
    button: {
      ...typography.button,
      textTransform: buttonTokens.textTransform,
    },
    display: typographyTokens.display,
    title: typographyTokens.title,
    heading: typographyTokens.heading,
    subheading: typographyTokens.subheading,
    label: typographyTokens.label,
    // overline keeps the MUI built-in from `...typography`; Text applies
    // typographyTokens.overline for the design-system role style.
    placeholder: typographyTokens.placeholder,
    helper: typographyTokens.helper,
    error: typographyTokens.error,
  },
  spacing: spacingUnit,
  shadows: [...shadows],
  breakpoints: {
    values: breakpointTokens,
  },
  transitions: {
    duration: {
      shortest: motionRoles.focus.duration,
      shorter: motionRoles.exit.duration,
      short: motionRoles.hover.duration,
      standard: motionRoles.enter.duration,
      complex: duration.slow,
      enteringScreen: motionRoles.enter.duration,
      leavingScreen: motionRoles.exit.duration,
    },
    easing: {
      easeInOut: motionRoles.hover.easing,
      easeOut: motionRoles.enter.easing,
      easeIn: motionRoles.exit.easing,
      sharp: motionRoles.focus.easing,
    },
  },
  shape: {
    borderRadius: radii.sm,
  },
  zIndex: {
    ...zIndexTokens,
    fab: zIndexTokens.speedDial,
  },
  palette: {
    primary: {
      main: primaryColor,
      contrastText: contrastWhite,
    },
    secondary: {
      main: secondaryColor,
      contrastText: contrastWhite,
    },
    cancel: {
      main: cancelColor,
      contrastText: contrastWhite,
    },
    inputButton: {
      main: colorPalette.extended.greyLight,
      dark: colorPalette.extended.greyMedium,
      contrastText: color.text.primary,
    },
    default: {
      main: colorPalette.neutral[800],
      contrastText: contrastWhite,
    },
    text: {
      primary: color.text.primary,
      secondary: color.text.secondary,
      disabled: color.text.disabled,
    },
    background: {
      default: color.surface.default,
      paper: color.surface.elevated,
    },
    success: color.feedback.success,
    warning: color.feedback.warning,
    error: color.feedback.error,
    info: color.feedback.info,
    divider: color.border.divider,
  },
  components: {
    MuiDivider: {
      styleOverrides: {
        root: {
          borderBottomWidth: dividerTokens.width,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: buttonTokens.disableElevation,
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          "&:focus-visible": {
            outline: `${focusRing.width}px ${focusRing.style} ${focusRing.color}`,
            outlineOffset: focusRing.offset,
          },
          textTransform: buttonTokens.textTransform,
          borderRadius: buttonTokens.borderRadius,
          ...(ownerState.size === "small" && {
            minHeight: buttonTokens.height.small,
            padding: buttonTokens.padding.small,
          }),
          ...(ownerState.size === "medium" && {
            minHeight: buttonTokens.height.medium,
            padding: buttonTokens.padding.medium,
          }),
          ...(ownerState.size === "large" && {
            minHeight: buttonTokens.height.large,
            padding: buttonTokens.padding.large,
          }),
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: dialogTitlePadding,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: dialogBodyPadding,
          ".MuiDialogTitle-root + &": {
            paddingTop: `${layout.dialog.bodyPaddingY}px`,
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: dialogActionsPadding,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: { color: color.feedback.error.main },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&.MuiTableRow-hover": {
            "&:hover": {
              backgroundColor: colorPalette.extended.offWhite,
            },
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        ".material-icons,.material-icons-outlined": {
          "&.md-16": { fontSize: `${iconSize.xs}px` },
          "&.md-18": { fontSize: "18px" },
          "&.md-20": { fontSize: `${iconSize.sm}px` },
          "&.md-24": { fontSize: `${iconSize.md}px` },
          "&.md-36": { fontSize: `${iconSize.lg}px` },
          "&.md-48": { fontSize: `${iconSize.xl}px` },
          "&.md-dark": { color: iconColor.secondary },
          "&.md-dark.md-inactive": { color: iconColor.inactive },
          "&.md-light": { color: iconColor.onDark },
          "&.md-light.md-inactive": { color: iconColor.inactiveOnDark },
        },
        "mapbox-search-listbox": {
          position: "absolute",
          zIndex: 1000000,
        },
      },
    },
  },
};

export const AppTheme = createTheme(appThemeOptions);
